import { stableRecordKey } from '../lib/rowIdentity';
import React, { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { CheckCircle, Ticket, QrCode, Calendar, MapPin, Download, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Separator } from '../components/ui/separator';
import { api } from '../App';

export default function PaymentSuccess() {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');
  const orderId = searchParams.get('order_id');
  const [status, setStatus] = useState('polling');
  const [order, setOrder] = useState(null);
  const [tickets, setTickets] = useState([]);
  const [event, setEvent] = useState(null);
  const [attempts, setAttempts] = useState(0);

  useEffect(() => {
    // Direct pass view: /payment/success?order_id=... (paid orders only, no Stripe session needed)
    if (!sessionId && orderId) {
      (async () => {
        try {
          const orderData = await api.get(`/api/orders/${orderId}/tickets`);
          const st = orderData.order?.payment_status || orderData.order?.status;
          if (st === 'paid') {
            setOrder(orderData.order);
            setTickets(orderData.tickets || []);
            setEvent(orderData.event);
            setStatus('paid');
          } else {
            setStatus('error');
          }
        } catch {
          setStatus('error');
        }
      })();
      return;
    }
    if (!sessionId) { setStatus('error'); return; }
    const poll = async () => {
      try {
        const res = await api.get(`/api/payments/status/${sessionId}`);
        if (res.payment_status === 'paid') {
          setStatus('paid');
          if (orderId) {
            const orderData = await api.get(`/api/orders/${orderId}/tickets`);
            setOrder(orderData.order);
            setTickets(orderData.tickets || []);
            setEvent(orderData.event);
          }
          return;
        }
        if (res.status === 'expired') { setStatus('expired'); return; }
        if (attempts < 10) {
          setTimeout(() => setAttempts(a => a + 1), 2000);
        } else {
          setStatus('timeout');
        }
      } catch {
        if (attempts < 10) {
          setTimeout(() => setAttempts(a => a + 1), 2000);
        } else {
          setStatus('error');
        }
      }
    };
    poll();
  }, [sessionId, orderId, attempts]);

  if (status === 'polling') {
    return (
      <div className="max-w-2xl mx-auto px-4 py-20 text-center">
        <Loader2 className="w-12 h-12 animate-spin text-[hsl(var(--gs-saffron))] mx-auto mb-4" />
        <h2 className="text-xl font-semibold mb-2" style={{fontFamily: 'Space Grotesk'}}>Processing your payment...</h2>
        <p className="text-[hsl(var(--muted-foreground))]">Please wait while we confirm your payment.</p>
      </div>
    );
  }

  if (status !== 'paid') {
    return (
      <div className="max-w-2xl mx-auto px-4 py-20 text-center">
        <h2 className="text-xl font-semibold mb-2" style={{fontFamily: 'Space Grotesk'}}>Payment {status === 'expired' ? 'expired' : 'issue'}</h2>
        <p className="text-[hsl(var(--muted-foreground))] mb-4">There was an issue with your payment. Please check your email for confirmation or try again.</p>
        <Link to="/events"><Button>Back to Events</Button></Link>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 py-12 sm:py-16" data-testid="payment-success-page">
      <div className="text-center mb-8">
        <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-4">
          <CheckCircle className="w-8 h-8 text-green-600" />
        </div>
        <h1 className="text-3xl font-semibold mb-2" style={{fontFamily: 'Space Grotesk'}} data-testid="payment-success-title">{order?.complimentary || Number(order?.total_amount || 0) === 0 ? 'Your tickets are confirmed!' : 'Payment confirmed!'}</h1>
        <p className="text-[hsl(var(--muted-foreground))]">{order?.complimentary ? `These tickets are complimentary, with our thanks${order?.coupon_code ? ` (coupon ${order.coupon_code})` : ''} — there is nothing to pay.` : 'Thank you for your booking — your tickets are ready.'} A confirmation email is on its way to you.</p>
      </div>

      {event && (
        <Card className="mb-6">
          <CardContent className="p-6">
            <h3 className="text-lg font-semibold mb-2" style={{fontFamily: 'Space Grotesk'}}>{event.title}</h3>
            <div className="flex flex-wrap gap-4 text-sm text-[hsl(var(--muted-foreground))]">
              <div className="flex items-center gap-1"><Calendar className="w-4 h-4" /> {new Date(event.date).toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}</div>
              {event.venue && <div className="flex items-center gap-1"><MapPin className="w-4 h-4" /> {event.venue}</div>}
            </div>
            {order && (
              <div className="mt-3 flex items-center gap-2">
                <Badge variant="outline">Order: {order.order_id}</Badge>
                <Badge className="bg-[hsl(var(--gs-saffron-soft))] text-[hsl(var(--gs-ink))]">{order.complimentary ? 'Complimentary' : `Total: £${Number(order.total_amount || 0).toFixed(2)}`}</Badge>
                {!order.complimentary && order.discount_amount > 0 && <Badge variant="outline">Coupon {order.coupon_code}: −£{Number(order.discount_amount).toFixed(2)}</Badge>}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {tickets.length > 0 && (
        <div className="space-y-4" id="tickets-print-area">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <h2 className="text-lg font-semibold" style={{fontFamily: 'Space Grotesk'}}>
              {tickets.some(t => t.is_group) ? `Your ${tickets.length > 1 ? 'day passes' : 'group pass'} (${tickets.length})` : `Your tickets (${tickets.length})`}
            </h2>
            <Button variant="outline" size="sm" onClick={() => window.print()} data-testid="print-tickets-button">
              <Download className="w-4 h-4 mr-1" /> Print / Save as PDF
            </Button>
          </div>
          {tickets.some(t => t.is_group) && (
            <p className="text-sm text-[hsl(var(--muted-foreground))]">
              One QR code per day covers your whole group — a single scan at the gate admits everyone listed on that pass.
            </p>
          )}
          {tickets.map(ticket => (
            <Card key={ticket.ticket_id} className="overflow-hidden border-2 border-[hsl(var(--gs-saffron)/0.3)]" data-testid="payment-success-ticket">
              <div className="bg-[hsl(var(--gs-navy))] text-white px-6 py-3 flex items-center justify-between flex-wrap gap-2">
                <div className="flex items-center gap-2">
                  <Ticket className="w-4 h-4 text-[hsl(var(--gs-saffron))]" />
                  <span className="font-semibold" style={{fontFamily: 'Space Grotesk'}}>
                    {ticket.event_day
                      ? new Date(ticket.event_day + 'T00:00:00').toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })
                      : `${String(ticket.ticket_type || '').replace('_', ' ')} Ticket`}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  {ticket.is_season && <Badge className="bg-[hsl(var(--gs-saffron))] text-white text-xs">Season</Badge>}
                  {ticket.admit_count > 0 && (
                    <Badge className="bg-white text-[hsl(var(--gs-navy))] font-bold text-sm px-3" data-testid="ticket-admit-badge">
                      ADMIT {ticket.admit_count}
                    </Badge>
                  )}
                </div>
              </div>
              <CardContent className="p-6">
                <div className="flex flex-col sm:flex-row gap-6">
                  <div className="flex-1">
                    {ticket.attendees?.length > 0 ? (
                      <div className="space-y-2">
                        <div className="text-xs font-semibold uppercase tracking-wide text-[hsl(var(--muted-foreground))]">Group members</div>
                        {ticket.attendees.map((a, i) => (
                          <div key={stableRecordKey(a)} className="flex items-center gap-2 text-sm">
                            <span className="w-5 h-5 rounded-full bg-[hsl(var(--gs-saffron-soft))] text-[hsl(var(--gs-ink))] text-[10px] font-bold flex items-center justify-center">{i + 1}</span>
                            <span className="font-medium">{a.name}</span>
                            <Badge variant="outline" className="text-[10px]">{a.category_name}</Badge>
                            {a.gender && <span className="text-xs text-[hsl(var(--muted-foreground))] capitalize">{a.gender}</span>}
                          </div>
                        ))}
                        {ticket.attendees.some(a => a.student_id || a.category_name?.toLowerCase().includes('student')) && (
                          <p className="text-[11px] text-amber-700 mt-1">Students: please carry your physical student ID to the event.</p>
                        )}
                      </div>
                    ) : (
                      <div className="text-sm text-[hsl(var(--muted-foreground))] space-y-1">
                        <div className="flex items-center gap-2">
                          <span className="font-semibold capitalize text-[hsl(var(--foreground))]" style={{fontFamily: 'Space Grotesk'}}>{ticket.ticket_type?.replace('_', ' ')} Ticket</span>
                          {ticket.is_member && <Badge className="bg-[hsl(var(--gs-saffron-soft))] text-[hsl(var(--gs-ink))] text-xs">Member</Badge>}
                        </div>
                        <div>Name: {ticket.attendee_name}</div>
                        {ticket.membership_number && <div>Membership: {ticket.membership_number}</div>}
                      </div>
                    )}
                    <div className="mt-3 text-xs text-[hsl(var(--muted-foreground))]">Ticket ID: <span className="font-mono">{ticket.ticket_id}</span></div>
                  </div>
                  {ticket.qr_code && (
                    <div className="flex flex-col items-center" data-testid="payment-success-ticket-qr">
                      <div className="w-40 h-40 border-2 border-[hsl(var(--gs-saffron)/0.4)] rounded-xl p-2 bg-white">
                        <img src={`data:image/png;base64,${ticket.qr_code}`} alt="QR Code" className="w-full h-full" />
                      </div>
                      <span className="text-xs text-[hsl(var(--muted-foreground))] mt-1">One scan admits {ticket.admit_count > 1 ? `all ${ticket.admit_count}` : 'entry'}</span>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <div className="mt-8 flex flex-wrap gap-3 justify-center">
        <Link to="/events"><Button variant="outline">Browse more events</Button></Link>
      </div>
    </div>
  );
}
